/**
 * Quest Tool
 * In-game quest editor/viewer for creating and managing quests at runtime
 * Integrated into debug menu for easy quest development
 */

import { useState, useMemo, useEffect } from 'react';
import { marketManager } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { GameEvent } from '@/types';
import { QuestEditor } from './QuestEditor';
import { QuestListPanel } from './QuestListPanel';

interface QuestToolProps {
  onClose: () => void;
}

export function QuestTool({ onClose }: QuestToolProps) {
  const [selectedQuestId, setSelectedQuestId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Auto-refresh when quests or chains are updated
  useEffect(() => {
    const unsubQuest = eventBus.on(GameEvent.CUSTOM_QUEST_UPDATED, () => {
      marketManager.reloadAllQuests();
      setRefreshKey(prev => prev + 1);
    });

    const unsubChain = eventBus.on(GameEvent.QUEST_CHAIN_UPDATED, () => {
      setRefreshKey(prev => prev + 1);
    });

    return () => {
      unsubQuest();
      unsubChain();
    };
  }, []);

  // Get all quests (default + custom)
  const allQuests = useMemo(() => {
    return marketManager.getAllQuestDefinitions();
  }, [refreshKey]);

  const selectedQuest = selectedQuestId
    ? allQuests.find((q) => q.id === selectedQuestId)
    : null;

  const handleNewQuest = () => {
    setIsCreatingNew(true);
    setSelectedQuestId(null);
  };

  const handleSelectQuest = (questId: string) => {
    setSelectedQuestId(questId);
    setIsCreatingNew(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border-2 border-blue-500 w-full max-w-7xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📋</span>
            <div>
              <h2 className="text-2xl font-bold text-white">Quest Tool</h2>
              <p className="text-sm text-gray-400">
                Create, edit, and manage quests at runtime
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded font-bold transition-colors"
          >
            ✕ Close
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel: Quest List with Chains */}
          <div className="w-1/3 border-r border-gray-700">
            <QuestListPanel
              quests={allQuests}
              selectedQuestId={selectedQuestId}
              onSelectQuest={handleSelectQuest}
              onNewQuest={handleNewQuest}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>

          {/* Right Panel: Quest Editor */}
          <div className="flex-1 overflow-y-auto p-6">
            {isCreatingNew && (
              <QuestEditor
                quest={null}
                onSave={() => setIsCreatingNew(false)}
                onCancel={() => setIsCreatingNew(false)}
              />
            )}

            {!isCreatingNew && selectedQuest && (
              <QuestEditor
                quest={selectedQuest}
                onSave={() => { }}
                onCancel={() => setSelectedQuestId(null)}
              />
            )}

            {!isCreatingNew && !selectedQuest && (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <p className="text-6xl mb-4">📋</p>
                  <p className="text-lg">Select a quest to view/edit</p>
                  <p className="text-sm">or create a new quest</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

